// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("360wallet_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("360wallet_token");
      window.location.href = "/admin/login";
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");
export const getRecentTransactions = (limit = 10) => api.get(`/dashboard/recent-transactions?limit=${limit}`);
export const getTransactionChart = (days = 7) => api.get(`/dashboard/transaction-chart?days=${days}`);
export const getIssuerBreakdown = () => api.get("/dashboard/issuer-breakdown");
export const getRecentWithdrawals = (limit = 20) => api.get(`/dashboard/recent-withdrawals?limit=${limit}`);

// Banks
export const getBanks = (params = {}) => api.get("/banks", { params });
export const getBank = (id) => api.get(`/banks/${id}`);
export const createBank = (data) => api.post("/banks", data);
export const updateBank = (id, data) => api.patch(`/banks/${id}`, data);
export const deleteBank = (id) => api.delete(`/banks/${id}`);

// Retail Stores
export const getRetailStores = (params = {}) => api.get("/retail-stores", { params });
export const getRetailStore = (code) => api.get(`/retail-stores/${code}`);
export const createRetailStore = (data) => api.post("/retail-stores", data);
export const updateRetailStore = (code, data) => api.put(`/retail-stores/${code}`, data);
export const deleteRetailStore = (code) => api.delete(`/retail-stores/${code}`);

// Authorizations
export const getAuthorizations = (params = {}) => api.get("/authorizations", { params });
export const getAuthorization = (id) => api.get(`/authorizations/${id}`);
export const createAuthorization = (data) => api.post("/authorizations", data);
export const cancelAuthorization = (id) => api.post(`/authorizations/${id}/cancel`);
export const lookupAuthorization = (code) => api.get(`/authorizations/lookup/${code}`);

// ATM Withdrawal (Mock)
export const processATMWithdrawal = (data) => api.post("/atm/withdraw", data);

// Transactions
export const getTransactions = (params = {}) => api.get("/transactions", { params });
export const getTransaction = (id) => api.get(`/transactions/${id}`);

// Deposits
export const getDeposits = (params = {}) => api.get("/deposits", { params });
export const getDeposit = (id) => api.get(`/deposits/${id}/status`);

// Pricing
export const getPricingConfigs = (params = {}) => api.get("/pricing", { params });
export const getActiveRates = () => api.get("/pricing/active-rates");
export const createPricingConfig = (data) => api.post("/pricing", data);
export const updatePricingConfig = (id, isActive) => api.patch(`/pricing/${id}?is_active=${isActive}`);

// Settlements
export const getSettlements = (params = {}) => api.get("/settlements", { params });
export const generateSettlement = (date) => api.post(`/settlements/generate?settlement_date=${date}`);
export const updateSettlementStatus = (id, status) => api.patch(`/settlements/${id}/status?status=${status}`);
export const exportSettlements = (date) => api.get(`/settlements/export?settlement_date=${date}`);
export const exportSettlementsPDF = (date) => api.get(`/settlements/export-pdf?settlement_date=${date}`, { responseType: 'blob' });
export const generateMonthlyBilling = (bankCode, month) => api.get(`/settlements/monthly-billing/${bankCode}?month=${month}`, { responseType: 'blob' });

// Settlement Account (Platform Beneficiary)
export const getSettlementAccount = () => api.get("/settlement-account");
export const createSettlementAccount = (data) => api.post("/settlement-account", data);
export const updateSettlementAccount = (data) => api.put("/settlement-account", data);
export const deleteSettlementAccount = () => api.delete("/settlement-account");

// Fee Schedules
export const getFeeSchedules = (params = {}) => api.get("/fee-schedules", { params });
export const getFeeSchedule = (id) => api.get(`/fee-schedules/${id}`);
export const createFeeSchedule = (data) => api.post("/fee-schedules", data);
export const updateFeeSchedule = (id, data) => api.put(`/fee-schedules/${id}`, data);
export const deleteFeeSchedule = (id) => api.delete(`/fee-schedules/${id}`);
export const approveFeeSchedule = (id) => api.post(`/fee-schedules/${id}/approve`);

// Tier 3 Operators (ATM Acquirers)
export const getTier3Operators = (params = {}) => api.get("/billing/tier3-operators", { params });
export const createTier3Operator = (data) => api.post("/billing/tier3-operators", data);
export const updateTier3Operator = (id, data) => api.put(`/billing/tier3-operators/${id}`, data);
export const deleteTier3Operator = (id) => api.delete(`/billing/tier3-operators/${id}`);

// Billing Profiles
export const getBillingProfiles = (params = {}) => api.get("/billing-profiles", { params });
export const getBillingProfile = (type, id) => api.get(`/billing-profiles/${type}/${id}`);
export const createBillingProfile = (type, id, data) => api.post(`/billing-profiles/${type}/${id}`, data);
export const deleteBillingProfile = (type, id) => api.delete(`/billing-profiles/${type}/${id}`);

// Fee Calculation
export const calculateFee = (params) => api.get("/fee-calculation", { params });

// Webhooks
export const getWebhookDeliveries = (params = {}) => api.get("/webhooks/deliveries", { params });
export const retryFailedWebhooks = (maxAgeHours = 24) => api.post(`/webhooks/retry-failed?max_age_hours=${maxAgeHours}`);
export const testWebhook = (bankCode) => api.post(`/webhooks/test/${bankCode}`);

// Branding
export const getBranding = () => api.get("/config/branding");
export const updateBranding = (data) => api.put("/config/branding", data);

// Admin Users
export const createAdminUser = (data) => api.post("/auth/create-user", data);
export const listAdminUsers = () => api.get("/auth/users");

// Two-Factor Authentication
export const get2FAStatus = () => api.get("/auth/2fa/status");
export const setup2FA = () => api.post("/auth/2fa/setup");
export const verify2FA = (totpCode) => api.post("/auth/2fa/verify", { totp_code: totpCode });
export const disable2FA = (totpCode) => api.post("/auth/2fa/disable", { totp_code: totpCode });
export const regenerateBackupCodes = (totpCode) => api.post("/auth/2fa/regenerate-backup-codes", { totp_code: totpCode });
export const changePassword = (data) => api.post("/auth/change-password", data);

// Settlement Ledger
export const getSettlementLedger = (params = {}) => api.get("/settlement-ledger", { params });
export const getSettlementSummary = (params = {}) => api.get("/settlement-ledger/summary", { params });
export const settleEntry = (entryId) => api.patch(`/settlement-ledger/${entryId}/settle`);
export const bulkSettle = (data) => api.post("/settlement-ledger/bulk-settle", null, { params: data });

// Latency Monitoring
export const getLatencyMetrics = () => api.get("/latency/metrics");
export const getLatencyHistory = (limit = 100) => api.get(`/latency/history?limit=${limit}`);
export const getLatencyBreakdown = (withdrawalId) => api.get(`/latency/breakdown/${withdrawalId}`);
export const getSlaReport = (days = 7) => api.get(`/latency/sla-report?days=${days}`);

// Security
export const getSecurityStatus = () => api.get("/security/status");
export const getRateLimitConfig = () => api.get("/security/rate-limits");

// Simulation
export const getSimulationConfig = () => api.get("/simulation/config");
export const toggleSimulation = (enabled, scenario = "normal") => api.post(`/simulation/toggle?enabled=${enabled}&scenario=${scenario}`);
export const runSimulationBatch = (count = 10, scenario = "normal") => api.post(`/simulation/run-batch?count=${count}&scenario=${scenario}`);

// mTLS Certificate Management
export const getMtlsStatus = () => api.get("/mtls/status");
export const getMtlsInstitutions = () => api.get("/mtls/institutions");
export const registerMtlsInstitution = (name, role, id, days = 365) => api.post(`/mtls/institutions?institution_name=${encodeURIComponent(name)}&role=${role}&institution_id=${encodeURIComponent(id)}&validity_days=${days}`);
export const revokeMtlsCert = (dn, reason = "administrative") => api.post("/mtls/revoke", null, { params: { institution_dn: dn, reason } });
export const reinstateMtlsCert = (dn) => api.post("/mtls/reinstate", null, { params: { institution_dn: dn } });
export const setMtlsEnforcement = (mode) => api.post(`/mtls/enforcement?mode=${mode}`);
export const testMtlsConnection = () => api.post("/mtls/test-connection");
export const getMtlsLogs = (limit = 50) => api.get(`/mtls/connection-logs?limit=${limit}`);

// OCSP & Certificate Management
export const getOcspStatus = () => api.get("/ocsp/status");
export const getOcspCompliance = () => api.get("/ocsp/compliance");
export const getOcspLogs = (limit = 100) => api.get(`/ocsp/logs?limit=${limit}`);
export const verifyCertificate = (serialHex) => api.get(`/ocsp/verify/${serialHex}`);
export const clearOcspCache = () => api.post("/ocsp/cache/clear");
export const regenerateCrl = () => api.post("/ocsp/generate-crl");

// Notifications
export const getNotifications = (limit = 50, unreadOnly = false) =>
  api.get(`/notifications?limit=${limit}&unread_only=${unreadOnly}`);
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post("/notifications/read-all");
export const triggerExpiryCheck = () => api.post("/notifications/check-expiry");
export const getEmailLog = (limit = 50) => api.get(`/notifications/email-log?limit=${limit}`);
export const getNotificationSettings = () => api.get("/notifications/settings");
export const updateNotificationSettings = (settings) => api.put("/notifications/settings", settings);

// Audit Logs
export const getAuditLogs = (params = {}) => api.get("/audit-logs", { params });

export default api;
